import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import { TaskModalComponentProps, formatLastAssignment } from './taskModal.types';
import * as Bixby from '$/core';
import { UserStatus, WmtTaskPriority } from '$/core/enums';
import { CompanyService } from '$/services/CompanyService';
import { AppState } from '$/store';
import { useAssignees } from './useAssignees';
import { useTaskNotes } from './useTaskNotes';
import TaskNotesSection from './TaskNotesSection';
import './taskModal.scss';

const ASSIGNEE_STATUS_ORDER: UserStatus[] = [
  UserStatus.Available,
  UserStatus.Break,
  UserStatus.Busy,
  UserStatus.OutOfOffice,
];

const ASSIGNEE_STATUS_BACKGROUND_MAP: Record<UserStatus, React.CSSProperties> = {
  [UserStatus.Available]: { backgroundColor: '#ecfdf3', color: '#374151' },
  [UserStatus.Break]: { backgroundColor: '#fffbeb', color: '#374151' },
  [UserStatus.Busy]: { backgroundColor: '#fef2f2', color: '#374151' },
  [UserStatus.OutOfOffice]: { backgroundColor: '#f3f4f6', color: '#374151' },
  [UserStatus.NoTasks]: { backgroundColor: '#eff6ff', color: '#374151' },
};

const PRIORITY_STYLE_MAP: Record<WmtTaskPriority, React.CSSProperties> = {
  [WmtTaskPriority.Urgent]: { backgroundColor: '#fef2f2', color: '#374151' },
  [WmtTaskPriority.Important]: { backgroundColor: '#fff7ed', color: '#374151' },
  [WmtTaskPriority.Medium]: { backgroundColor: '#eff6ff', color: '#374151' },
  [WmtTaskPriority.Low]: { backgroundColor: '#f0fdf4', color: '#374151' },
};

const MissingEmailTaskModal: React.FC<TaskModalComponentProps> = ({
  isOpen,
  taskData,
  assigneeList,
  onClose,
  onTaskCreated,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>('');
  const [selectedDueDate, setSelectedDueDate] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<WmtTaskPriority>(WmtTaskPriority.Medium);
  const [lastTask, setLastTask] = useState<Bixby.Models.ClientCompanyTask | null>(null);
  const [isLoadingLastTask, setIsLoadingLastTask] = useState(false);
  const [draftNote, setDraftNote] = useState('');
  const { assignees, isLoadingAssignees } = useAssignees(isOpen, assigneeList);
  const { notes, addNote } = useTaskNotes(taskData.groupId);

  // Derive current user's display name from Redux profile
  const profile = useSelector((state: AppState) => state.profileState.profile);
  const currentUserName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || profile?.email || 'Unknown User';

  useEffect(() => {
    if (isOpen && taskData) {
      setIsLoadingLastTask(true);
      CompanyService.getMostRecentCompanyTask(taskData.companyId)
        .then(result => setLastTask(result))
        .catch(() => setLastTask(null))
        .finally(() => setIsLoadingLastTask(false));
    } else {
      setLastTask(null);
      setIsLoadingLastTask(false);
      setSelectedDueDate('');
    }
  }, [isOpen, taskData]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPriority(WmtTaskPriority.Medium);
      setDraftNote('');
    }
  }, [isOpen]);

  const handleCreate = useCallback(async () => {
    try {
      setIsSubmitting(true);
      // If the user typed a note, add it to the conversation and include it in the action payload.
      const noteText = draftNote.trim();
      if (noteText) {
        addNote(noteText, currentUserName);
        setDraftNote('');
      }
      await onTaskCreated(selectedAssigneeId || null, selectedDueDate || null, selectedPriority, noteText || undefined);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    onTaskCreated,
    onClose,
    selectedAssigneeId,
    selectedDueDate,
    selectedPriority,
    draftNote,
    addNote,
    currentUserName,
  ]);

  /**
   * Sends the current draft note as a standalone action (no task create/update).
   * The note is saved to the local conversation history immediately.
   */
  const handleSendNote = useCallback(() => {
    const noteText = draftNote.trim();
    if (!noteText) return;
    addNote(noteText, currentUserName);
    setDraftNote('');
  }, [draftNote, addNote, currentUserName]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) onClose();
  }, [isSubmitting, onClose]);

  if (!isOpen) return null;

  return (
    <Modal active={isOpen} onClose={handleClose}>
      <div className="create-task-modal">
        <h3 className="create-task-modal__title">Create Missing Email Task</h3>
        <p className="create-task-modal__description">
          Some clients have not yet received the documents. Create a task to track the missing deliveries.
        </p>

        <div className="create-task-modal__context-info">
          <div className="create-task-modal__context-item">
            <span className="create-task-modal__context-label">Bixby ID:</span>
            <span className="create-task-modal__context-value">{taskData.bixbyId}</span>
          </div>
          <div className="create-task-modal__context-item">
            <span className="create-task-modal__context-label">Data Site:</span>
            <span className="create-task-modal__context-value">{taskData.dataSiteName}</span>
          </div>
          <div className="create-task-modal__context-item">
            <span className="create-task-modal__context-label">Provider:</span>
            <span className="create-task-modal__context-value">{taskData.provider}</span>
          </div>
          {taskData.docNames?.length > 0 && (
            <div className="create-task-modal__context-item create-task-modal__context-item--documents">
              <span className="create-task-modal__context-label">Documents:</span>
              <div className="create-task-modal__document-pills">
                {taskData.docNames.map(doc => (
                  <span key={doc} className="create-task-modal__document-pill">
                    {doc}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="create-task-modal__missing-clients-section">
          <h4 className="create-task-modal__section-header">
            Missing Clients ({taskData.missingClients?.length ?? 0})
          </h4>
          <div className="create-task-modal__clients-list">
            {taskData.missingClients?.map(client => (
              <div key={client.id} className="create-task-modal__client-item">
                <span className="create-task-modal__client-badge">Missing</span>
                <span className="create-task-modal__client-name">{client.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="create-task-modal__last-assignment">
          {isLoadingLastTask ? (
            <span className="create-task-modal__last-assignment--loading">Loading previous assignment...</span>
          ) : lastTask ? (
            <span>{formatLastAssignment(lastTask)}</span>
          ) : (
            <span className="create-task-modal__last-assignment--empty">
              No previous assignment found for this Bixby ID.
            </span>
          )}
        </div>

        <div className="create-task-modal__priority-due-date-row">
          <div className="create-task-modal__priority-field">
            <label className="create-task-modal__assignee-label" htmlFor="missing-email-task-priority">
              Priority
            </label>
            <select
              id="missing-email-task-priority"
              className="create-task-modal__assignee-select"
              value={selectedPriority}
              onChange={e => setSelectedPriority(e.target.value as WmtTaskPriority)}
              disabled={isSubmitting}>
              {Object.values(WmtTaskPriority).map(p => (
                <option key={p} value={p} style={PRIORITY_STYLE_MAP[p]}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div className="create-task-modal__due-date-field">
            <label className="create-task-modal__assignee-label" htmlFor="missing-email-task-due-date">
              Due Date
            </label>
            <input
              id="missing-email-task-due-date"
              type="date"
              className="create-task-modal__assignee-select"
              value={selectedDueDate}
              onChange={e => setSelectedDueDate(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="create-task-modal__assignee-field">
          <label className="create-task-modal__assignee-label" htmlFor="missing-email-task-assignee">
            Assignee
          </label>
          <select
            id="missing-email-task-assignee"
            className="create-task-modal__assignee-select"
            value={selectedAssigneeId}
            onChange={e => setSelectedAssigneeId(e.target.value)}
            disabled={isSubmitting || isLoadingAssignees}>
            <option value="">{isLoadingAssignees ? 'Loading assignees…' : '— Select Assignee —'}</option>
            {ASSIGNEE_STATUS_ORDER.map(status => {
              const group = assignees.filter(a => a.status === status);
              if (group.length === 0) return null;
              const statusClass = status.toLowerCase().replaceAll(' ', '-');

              return (
                <optgroup
                  key={status}
                  label={status}
                  className={`create-task-modal__assignee-group create-task-modal__assignee-group--${statusClass}`}>
                  {group.map(assignee => (
                    <option
                      key={assignee.id}
                      value={assignee.id}
                      className={`create-task-modal__assignee-option create-task-modal__assignee-option--${statusClass}`}
                      style={ASSIGNEE_STATUS_BACKGROUND_MAP[status]}>
                      {assignee.name}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>

        <TaskNotesSection
          notes={notes}
          draftNote={draftNote}
          onDraftChange={setDraftNote}
          onSendNote={handleSendNote}
          disabled={isSubmitting}
        />

        <div className="create-task-modal__actions">
          <BixButton onClick={handleClose} disabled={isSubmitting} light>
            Cancel
          </BixButton>
          <BixButton onClick={handleCreate} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Task'}
          </BixButton>
        </div>
      </div>
    </Modal>
  );
};

export default MissingEmailTaskModal;
